<!--
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
-->
<!-- IP-NOTICE: PROTECTED -->
<div
	class="kaleida-upload"
	[class.kaleida-upload--disabled]="disabled()"
	[class.kaleida-upload--compact]="isCompact()"
	[class.kaleida-upload--image]="isImage()"
	[style.--upload-focus-colour]="configState().focusColour"
	[style.--upload-hover-border-colour]="configState().hoverBorderColour"
	[style.--upload-hover-background-colour]="configState().hoverBackgroundColour"
	[style.--upload-selected-row-background]="configState().selectedRowBackgroundColour">
	@if (configState().label) {
		<label class="kaleida-upload__label" [id]="controlId + '-label'" [for]="controlId + '-input'">{{ configState().label }}</label>
	}
	@if (configState().helpText) {
		<div class="kaleida-upload__help" [id]="controlId + '-help'">{{ configState().helpText }}</div>
	}

	<input
		#fileInput
		class="kaleida-upload__native-input"
		type="file"
		[id]="controlId + '-input'"
		[accept]="accept()"
		[multiple]="isMultiple()"
		[disabled]="disabled()"
		(change)="onInputChange($event)" />

	<div
		class="kaleida-upload__control"
		[class.kaleida-upload__control--drop]="showDropState()"
		[class.kaleida-upload__control--selected]="items().length > 0"
		[style.background-color]="items().length > 0 && isImage() ? configState().previewBackground : configState().backgroundColour"
		[attr.role]="isCompact() && items().length > 0 ? 'group' : 'button'"
		[tabindex]="disabled() || (isCompact() && items().length > 0) ? -1 : 0"
		[attr.aria-disabled]="disabled()"
		[attr.aria-labelledby]="configState().label ? controlId + '-label' : null"
		[attr.aria-label]="configState().label || configState().uploadPrompt"
		[attr.aria-describedby]="descriptionIds() || null"
		(click)="onControlClick()"
		(keydown)="onControlKeydown($event)"
		(pointerenter)="onPointerEnter()"
		(pointerleave)="onPointerLeave()"
		(dragenter)="onDragEnter($event)"
		(dragover)="onDragOver($event)"
		(dragleave)="onDragLeave($event)"
		(drop)="onDrop($event)">
		@if (isImage() && activeItem()) {
			<img
				class="kaleida-upload__preview"
				[src]="activeItem()!.previewUrl"
				[alt]="activeItem()!.name"
				[style.object-fit]="configState().imageFit" />
		} @else if (isCompact() && items().length > 0) {
			<div class="kaleida-upload__compact-file">
				<button class="kaleida-upload__compact-picker" type="button" [disabled]="disabled()" [attr.aria-label]="'Replace ' + items()[0].name" (click)="openPickerFromChild($event)">
					<orbit-icon aria-hidden="true" name="description"></orbit-icon>
					<span class="kaleida-upload__filename" [matTooltip]="items()[0].name">{{ items()[0].name }}</span>
				</button>
				<button mat-icon-button type="button" [disabled]="disabled()" [attr.aria-label]="'Remove ' + items()[0].name" (click)="removeItem(items()[0], $event)">
					<orbit-icon name="close"></orbit-icon>
				</button>
			</div>
		} @else {
			@if (configState().showIcon) {
				@if (activeIconLibrary() === UploadIconLibrary.Lucide) {
					<svg class="kaleida-upload__state-icon kaleida-upload__state-icon--lucide orbit-icon" [lucideIcon]="activeLucideIcon()" aria-hidden="true"></svg>
				} @else {
					<orbit-icon class="kaleida-upload__state-icon" aria-hidden="true" [name]="activeIconName()"></orbit-icon>
				}
			}
			<div class="kaleida-upload__prompt">{{ showDropState() ? configState().dropPrompt : configState().uploadPrompt }}</div>
		}
		@if (configState().supportingMessage) {
			<div class="kaleida-upload__support" [id]="controlId + '-support'">{{ configState().supportingMessage }}</div>
		}
		@if (restrictions()) {
			<div class="kaleida-upload__restrictions" [id]="controlId + '-restrictions'">{{ restrictions() }}</div>
		}
	</div>

	@if (!isCompact() && items().length > 0) {
		<div class="kaleida-upload__file-list" role="list" [attr.aria-label]="isImage() ? 'Selected images' : 'Selected files'">
			@for (item of items(); track item.key) {
				<div class="kaleida-upload__file-row" [class.kaleida-upload__file-row--active]="isImage() && activeKey() === item.key" role="listitem">
					<button
						class="kaleida-upload__file-details"
						type="button"
						[disabled]="disabled() || !isImage()"
						[attr.aria-label]="isImage() ? 'Show ' + item.name : item.name"
						[attr.aria-current]="isImage() && activeKey() === item.key ? 'true' : null"
						(click)="selectItem(item, $event)">
						<orbit-icon class="kaleida-upload__file-icon" name="description"></orbit-icon>
						<span class="kaleida-upload__file-meta">
							<span class="kaleida-upload__filename" [matTooltip]="item.name">{{ item.name }}</span>
							<span class="kaleida-upload__file-size">{{ formatFileSize(item.size) }}</span>
						</span>
					</button>
					<button class="kaleida-upload__file-remove" type="button" [disabled]="disabled()" [attr.aria-label]="'Remove ' + item.name" (click)="removeItem(item, $event)">
						<orbit-icon name="close"></orbit-icon>
					</button>
				</div>
			}
		</div>
	}

	@if (validationMessage()) {
		<div class="kaleida-upload__error" [id]="controlId + '-error'" role="alert">{{ validationMessage() }}</div>
	}
</div>
